import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Select, Tag } from 'antd';
import { useNotify } from 'react-admin';
import { useController } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { http } from '@network';
import { useIsMounted } from '@hooks';

const { Option } = Select;

const regex = /^[0-9]+$/;
const MAX_NAME_LENGTH = 60;

interface Props {
  source: string;
  reference?: string;
  optionKey?: string;
  optionLabel?: string;
  placeholder?: string;
  mainSource?: string;
  markedValue?: number;
  disabledIds?: number[];
  params?: any;
}

const ArrowIcon = ({ className }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg">
    <path d="M9 0C4.03021 0 0 4.03021 0 9C0 13.9698 4.03021 18 9 18C13.9698 18 18 13.9698 18 9C17.992 4.03021 13.9658 0.00399822 9 0Z" fill="#0C5541"/>
    <path d="M13.3301 7.25H10V15H8V7.25H4.66992L9 2L13.3301 7.25Z" fill="white"/>
  </svg>
);

export const ManyTagsInput = ({
  source, reference, optionKey, optionLabel, placeholder,
  mainSource, markedValue, disabledIds, params,
}: Props) => {
  const isMounted = useIsMounted();
  const notify = useNotify();
  const cls = useStyles();

  const {
    field: { value, onChange },
  } = useController({ name: source });

  const {
    field: { value: mainSourceValue, onChange: mainSourceOnChange },
  } = useController({ name: mainSource || 'mainSource' });

  const [options, setOptions] = useState<CheckOption[]>([]);
  const [forceOpened, setForceOpened] = useState<boolean>();
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const isDisabled = (id: string) => {
    return disabledIds?.includes(Number(id));
  };

  const handleChange = (ids: string[]) => {
    if (Array.isArray(ids)) {
      const numberIds = ids.filter(id => id);
      onChange(numberIds);
    }

    setSearchInput('');
  };

  const handleSearch = (value: string) => {
    setSearchInput(value);
    if (value.length > MAX_NAME_LENGTH) {
      notify(`${MAX_NAME_LENGTH} characters length is maximum.`, { type: 'error' });
    }
  };

  const filterOption = (input: string, option?: CheckOption) => {
    return option
      ? (option.label ?? '').toLowerCase().indexOf((input || '').toLowerCase()) >= 0
      : false;
  };

  const showAddButton =
    options.find(option => !(option.label || '')
      .toLowerCase()
      .startsWith((searchInput || '')
        .toLowerCase()))
    && !!searchInput.length
    && searchInput.length <= MAX_NAME_LENGTH;

  const handleSelect = (value: string) => {
    const isNewOption = options.findIndex(option => option.value === value) < 0;

    if (regex.test(value) && isNewOption) {
      notify('Only numbers are not allowed. Please use letters or a combination of letters and numbers.', {
        type: 'error',
      });
      setSelectedOptions(prevTags => prevTags.filter(option => {
        return option !== value;
      }));
    }
  };

  useEffect(() => {
    (async () => {
      const response = await http.get<any>(reference || source, {
        params: params ? params : { _start: 0, _end: 9999999 },
      });
      const nextOptions: CheckOption[] = [];
      if (response.data && Array.isArray(response.data)) {
        (response.data ?? []).forEach((model: any) => {
          const label = model[optionLabel ?? 'nameMx'] || model['name'] || model['title'];
          nextOptions.push({
            value: `${model[optionKey ?? 'id']}`,
            label,
          });
        });
      }
      isMounted.current && setOptions(nextOptions);
    })();
  }, [value]);

  useEffect(() => {
    setSelectedOptions((value || []).map((v: number) => `${v}`));
  }, [options]);

  return (
    <div className={cls.box}>
      <Select
        mode="tags"
        className={cls.select}
        placeholder={placeholder}
        value={selectedOptions}
        onChange={handleChange}
        onSearch={handleSearch}
        onSelect={handleSelect}
        filterOption={filterOption}
        open={forceOpened}
        style={{ maxWidth: '100%' }}
        onInputKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          const value = e.currentTarget.value;
          if (value.length >= MAX_NAME_LENGTH && e.key !== 'Backspace') {
            e.preventDefault();

            notify(`Company name with more than ${MAX_NAME_LENGTH} characters is not allowed.`, {
              type: 'error',
            });
          }
        }}
        tagRender={(props) => {
          const mainSourceSelected = `${props.value}` === `${mainSourceValue}`;
          const isNewOption = options.findIndex(option => option.value === props.value) < 0;

          return (
            <Tag
              defaultValue={props.value}
              closable={!isDisabled(props.value)}
              // closable={props.closable}
              onClose={props.onClose}
              className={cls.tag}
              style={{ borderColor: ((regex.test(props.value) || props.value.length > MAX_NAME_LENGTH) && isNewOption) ? 'red' : 'var(--logo-color)' }}
            >
              {mainSource && (
                <Checkbox
                  checked={mainSourceSelected}
                  onChange={(e) => {
                    e.target.checked && mainSourceOnChange(`${props.value}`);
                    setForceOpened(true);
                    [200, 300, 400].map(t => setTimeout(() => {
                      isMounted.current && setForceOpened(undefined);
                    }, t));
                  }}
                  className={cls.checkbox}
                />
              )}
              <span className={cls.content}>{props.label}</span>
            </Tag>
          );
        }}
        dropdownRender={(menu: any) => {
          return (
            <>
              {menu}
              {showAddButton
                ? <Button type="text" className={cls.btn}>+Add</Button>
                : null
              }
            </>
          );} }
      >
        {options.map(item => {
          const markedItem = `${item.value}` === `${markedValue}`;

          return (
            <Option
              key={item.value}
              value={item.value}
              label={item.label}
              disabled={isDisabled(item.value)}
              className={cls.option}>
              {item.label}
              {markedValue && markedItem ? (<ArrowIcon className={cls.mark} />) : null}
            </Option>
          );})}
      </Select>
      <ArrowDropDownIcon className={cls.suffixIcon} />
    </div>
  );
};

interface CheckOption {
  value: string,
  label: string,
}

const useStyles = makeStyles({
  box: {
    position: 'relative',
  },
  select: {
    width: '100%',
    maxWidth: '600px !important',
    '& .ant-select-selector': {
      border: 'none !important',
      borderBottom: '1px solid var(--logo-color) !important',
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
      borderRadius: '0 !important',
    },
  },
  mark: {
    marginLeft: 6,
  },
  leftIcon: {
    marginRight: 6,
  },
  option: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  suffixIcon: {
    display: 'inline-block',
    position: 'absolute',
    color: 'var(--primary-color)',
    top: 2,
    right: -6,
  },
  tag: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginRight: 5,
    marginBottom: 3,
    maxWidth: '100%',
    background: 'var(--background-color) !important',
    color: 'var(--text-color) !important',
  },
  checkbox: {
    marginRight: 5,
  },
  content: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    webkitLineClamp: 1,
    webkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  btn: {
    position: 'absolute',
    top: 3,
    right: 0,
    color: 'var(--mutted-color)',
  },
  error: {
    color: 'red',
  },
});
